// React
import {
	forwardRef,
	ForwardRefExoticComponent,
	ChangeEvent,
	useState,
	useEffect,
} from "react"

// UI
import { cn } from "@/lib/utils"
import { InputProps } from "@/components/form-controls/Input"

// IBAN validation and formatting
import * as ibantools from "ibantools"

type IBANInputProps = Omit<InputProps, "onChange" | "value"> & {
	value?: string
	onChange?: (value: string) => void
	formatDisplay?: boolean
}

/**
 * Formats an IBAN for display by adding spaces every 4 characters
 */
const formatIBAN = (value: string): string => {
	return ibantools.friendlyFormatIBAN(value) ?? value
}

/**
 * Removes formatting from an IBAN
 */
const unformatIBAN = (value: string): string => {
	return (
		ibantools.electronicFormatIBAN(value) ??
		value.replace(/\s/g, "").toUpperCase()
	)
}

/**
 * IBANInput component for formatted IBAN entry with validation
 *
 * Features:
 * - Automatic formatting using ibantools library
 * - Country-specific length validation via ibantools
 * - IBAN checksum validation using mod-97 algorithm
 * - Visual feedback for valid/invalid IBANs
 * - Uppercase conversion
 * - Paste support
 * - Supports all IBAN countries
 *
 * @example
 * ```tsx
 * <IBANInput
 *   value={iban}
 *   onChange={(value) => setIban(value)}
 *   hasError={!!errors.iban}
 * />
 * ```
 */
const IBANInput: ForwardRefExoticComponent<IBANInputProps> = forwardRef<
	HTMLInputElement,
	IBANInputProps
>(
	(
		{
			className,
			hasError,
			value = "",
			onChange,
			formatDisplay = true,
			placeholder = "NL00BANK0000000000",
			maxLength = 42, // Maximum IBAN length (34) + spaces for formatting
			...props
		},
		ref,
	) => {
		const [displayValue, setDisplayValue] = useState("")
		const [isValid, setIsValid] = useState<boolean | null>(null)
		const [isFocused, setIsFocused] = useState(false)

		// Update display value when prop value changes
		useEffect(() => {
			setDisplayValue(formatDisplay ? formatIBAN(value) : value)

			// Validate IBAN when value changes
			const cleaned = unformatIBAN(value)
			if (cleaned.length >= 15) {
				const validationResult = ibantools.validateIBAN(cleaned)
				setIsValid(validationResult.valid)
			} else {
				setIsValid(null)
			}
		}, [value, formatDisplay])

		const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
			const input = e.target
			let newValue = input.value.toUpperCase()
			const cursorPosition = input.selectionStart ?? 0

			// Remove any non-alphanumeric characters
			newValue = newValue.replace(/[^A-Z0-9]/g, "")

			// Don't allow more than maximum possible IBAN length (34 is the max according to ibantools)
			if (newValue.length > 34) return

			// Format for display
			const formatted = formatDisplay ? formatIBAN(newValue) : newValue

			// Calculate new cursor position after formatting
			if (formatDisplay && ref && "current" in ref && ref.current) {
				// Count how many characters before cursor position
				const charsBeforeCursor = input.value
					.slice(0, cursorPosition)
					.replace(/[^A-Z0-9]/g, "").length

				setTimeout(() => {
					if (!ref.current) return

					// Find position in formatted string
					let newPos = 0
					let charCount = 0
					for (let i = 0; i < formatted.length; i++) {
						if (formatted[i] !== " ") {
							charCount++
						}
						if (charCount === charsBeforeCursor) {
							newPos = i + 1
							break
						}
					}
					// If we reached the end
					if (charCount < charsBeforeCursor) {
						newPos = formatted.length
					}

					ref.current.setSelectionRange(newPos, newPos)
				}, 0)
			}

			setDisplayValue(formatted)
			onChange?.(newValue)
		}

		const handleFocus = () => {
			setIsFocused(true)
			// Keep formatting while focused for consistent experience
		}

		const handleBlur = () => {
			setIsFocused(false)
			// Ensure formatting is applied on blur
			if (formatDisplay) {
				setDisplayValue(formatIBAN(value))
			}
		}

		const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
			e.preventDefault()
			const pastedText = e.clipboardData.getData("text")
			const cleaned = unformatIBAN(
				pastedText.toUpperCase().replace(/[^A-Z0-9]/g, ""),
			)

			// Don't allow paste if it would exceed max length
			if (cleaned.length > 34) {
				onChange?.(cleaned.slice(0, 34))
			} else {
				onChange?.(cleaned)
			}
		}

		// Determine border color based on validation state
		const getBorderColor = () => {
			if (hasError) return "border-red-300"
			if (isValid === true && !isFocused) return "border-green-500"
			if (isValid === false && !isFocused && value.length > 14)
				return "border-red-300"
			return "border-gray-300"
		}

		const getFocusColor = () => {
			if (hasError || (isValid === false && value.length > 14)) {
				return "focus:ring-red-500 focus:border-red-500"
			}
			if (isValid === true) {
				return "focus:ring-green-500 focus:border-green-500"
			}
			return "focus:ring-primary-500 focus:border-primary-500"
		}

		return (
			<div className="relative">
				<input
					ref={ref}
					type="text"
					value={displayValue}
					onChange={handleChange}
					onFocus={handleFocus}
					onBlur={handleBlur}
					onPaste={handlePaste}
					placeholder={formatIBAN(placeholder)}
					maxLength={maxLength}
					className={cn(
						"appearance-none",
						"border",
						getBorderColor(),
						hasError && "text-red-600",
						"shadow-sm",
						"placeholder-gray-400",
						"focus-within:text-dark focus:outline-none",
						getFocusColor(),
						"text-sm disabled:bg-gray-100",
						"font-mono", // Monospace font for better IBAN readability
						className,
					)}
					{...props}
				/>
				{/* Optional validation indicator */}
				{!isFocused && value.length > 14 && (
					<div className="absolute right-2 top-1/2 -translate-y-1/2">
						{isValid === true && (
							<svg
								className="size-5 text-green-500"
								fill="none"
								viewBox="0 0 24 24"
								stroke="currentColor"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M5 13l4 4L19 7"
								/>
							</svg>
						)}
						{isValid === false && (
							<svg
								className="size-5 text-red-500"
								fill="none"
								viewBox="0 0 24 24"
								stroke="currentColor"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M6 18L18 6M6 6l12 12"
								/>
							</svg>
						)}
					</div>
				)}
			</div>
		)
	},
)

IBANInput.displayName = "IBANInput"

// Export component
export { IBANInput }
